import { Calendar, Clock, Mail, Shield, ShieldCheck, User } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMeDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useUser } from "@/lib/user-context";

import { ActivityList } from "./activity-list";
import { PasswordForm } from "./password-form";
import { ProfileForm } from "./profile-form";

function getInitials(firstName: string | null, lastName: string | null, email: string): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  return (email[0] ?? "?").toUpperCase();
}

export function YouPage() {
  const { user } = useUser();
  const { data: detail } = useMeDetail(!!user);

  if (!user) {
    return <div className="text-muted py-12 text-center text-sm">Loading...</div>;
  }

  const fullName =
    user.first_name || user.last_name
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : user.email.split("@")[0];

  return (
    <div className="mx-auto max-w-2xl px-6 py-8">
      {/* Profile header */}
      <div className="flex items-center gap-4 pb-6">
        <Avatar size="lg">
          <AvatarFallback className="bg-accent-light text-primary text-sm font-semibold">
            {getInitials(user.first_name, user.last_name, user.email)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h1 className="text-foreground truncate text-lg font-normal">{fullName}</h1>
          <p className="text-muted truncate text-sm">{user.email}</p>
        </div>
        <Badge variant={user.role === "admin" ? "default" : "secondary"} className="shrink-0">
          {user.role === "admin" ? (
            <ShieldCheck className="mr-1 size-3" />
          ) : (
            <User className="mr-1 size-3" />
          )}
          {user.role}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile">
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="password">Password</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6 pt-4">
          {/* Read-only info */}
          <div className="space-y-3">
            <InfoRow icon={Mail} label="Email">
              {user.email}
              {user.email_verified ? (
                <Badge
                  variant="outline"
                  className="ml-2 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
                >
                  Verified
                </Badge>
              ) : (
                <Badge variant="destructive" className="ml-2">
                  Unverified
                </Badge>
              )}
            </InfoRow>
            <InfoRow icon={Shield} label="Role">
              {user.role === "admin" ? "Administrator" : "User"}
            </InfoRow>
          </div>

          {detail && (
            <div className="border-border rounded-lg border p-3">
              <h4 className="text-foreground mb-2 text-xs font-medium">Timeline</h4>
              <div className="space-y-2">
                <DateRow icon={Calendar} label="Joined" value={formatDate(detail.created_at)} />
                <DateRow
                  icon={Clock}
                  label="Last active"
                  value={formatDate(detail.last_active_at)}
                />
              </div>
            </div>
          )}

          {/* Editable name form */}
          <ProfileForm />
        </TabsContent>

        <TabsContent value="password" className="pt-4">
          <PasswordForm />
        </TabsContent>

        <TabsContent value="activity" className="pt-4">
          <ActivityList />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="text-muted size-4 shrink-0" />
      <span className="text-muted w-14 shrink-0">{label}</span>
      <span className="text-foreground flex items-center">{children}</span>
    </div>
  );
}

function DateRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted flex items-center gap-1.5">
        <Icon className="size-3" />
        {label}
      </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}
